import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { App } from '@capacitor/app'
import { OneSignal } from '@capacitor/one-signal'
import { useUser } from '@clerk/clerk-react'

// Utils
import { platform } from '@utils/capacitor-helper'
import { getLastLaunchUrl, setLastLaunchUrl } from '@utils/launch-url-storage'

/** 
 * This hook is in charge of handling the redirection of the user if the app is either opened
 * from a killed state or from the background with a link. The link can either be an actual link
 * or it can be provided from with a push notification.
 */
export const useLaunchUrlHandler = () => {
    const { isLoaded, isSignedIn } = useUser()
    const navigate = useNavigate()

    const handleUrl = useMemo(() => {
        if (platform === 'web') return false
        if (!isLoaded) return false

        return true
    }, [isLoaded, platform])

    // This function handles the redirection after it sanitizes the url so that
    // we are only working with the path.
    const handleUrlRedirection = useCallback(async (url?: string | null, source: 'event' | 'initial' = 'event') => {
        if (!url) return

        const sanitizedPath = url
            .replace(/^https?:\/\/(?:dev\.)?group\.splitcard\.com/i, '')
            .replace(/^:\/\//, '/')
            .replace(/^\/+/, '/')
            .trim()

        let targetPath = sanitizedPath.length > 0 ? sanitizedPath : '/'

        // If the user is signed in, don't navigate to auth pages — redirect to /account instead.
        // This prevents a white screen when opening a deep link like /sign-in?_gl=... while signed in.
        const pathWithoutQuery = targetPath.split('?')[0]
        const isAuthPath = pathWithoutQuery === '/' || pathWithoutQuery.startsWith('/sign-in') || pathWithoutQuery.startsWith('/verify')
        if (isSignedIn && isAuthPath) {
            targetPath = '/account'
        }

        if (source === 'initial') {
            const lastLaunchUrl = await getLastLaunchUrl()
            if (lastLaunchUrl === targetPath) return
        }

        await setLastLaunchUrl(targetPath)
        Promise.resolve().then(() => navigate(targetPath, { replace: true }))
    }, [navigate, isSignedIn])

    const hasHandledInitialLaunchRef = useRef(false)
    useEffect(() => {
        if (!handleUrl || hasHandledInitialLaunchRef.current) return

        let isCanceled = false

        async function resolveLaunchUrl() {
            const [capacitorResult, oneSignalResult] = await Promise.allSettled([
                App.getLaunchUrl(),
                OneSignal.getLaunchUrl()
            ])

            if (oneSignalResult.status === 'fulfilled' && oneSignalResult.value?.url) {
                handleUrlRedirection(oneSignalResult.value.url, 'event')
                return
            }

            if (capacitorResult.status === 'fulfilled' && capacitorResult.value?.url) {
                handleUrlRedirection(capacitorResult.value.url, 'initial')
            }
        }

        if (isCanceled) return

        resolveLaunchUrl()
            .catch(error => console.log('[ useLaunchUrlHandlers ] resolveLaunchUrl', error))
            .finally(() => {
                if (!isCanceled) hasHandledInitialLaunchRef.current = true
            })

        return () => {
            isCanceled = true
        }
    }, [handleUrlRedirection, handleUrl])

    // This useEffect will handle if the app is opened from the backend with a plain link. i.e. it will
    // use the `App.addListener('appUrlOpen')` listener.
    useEffect(() => {
        if (!handleUrl) return

        const listener = App.addListener('appUrlOpen', ({ url }) => {
            handleUrlRedirection(url, 'event')
        })

        return () => {
            listener
                .then(handle => handle.remove())
                .catch(error => console.error('[ useLaunchUrlHandlers ] App.addListener(\'appUrlOpen\')', error))
        }
    }, [handleUrlRedirection, handleUrl])

    // This useEffect will handle if the app is opened from the backend with a plain link. i.e. it will
    // use the `OneSignal.addListener('onClick')` listener.
    useEffect(() => {
        if (!handleUrl) return

        const listener = OneSignal.addListener('onClick', ({ url }) => {
            handleUrlRedirection(url, 'event')
        })

        return () => {
            listener
                .then(handle => handle.remove())
                .catch(error => console.error('[ useLaunchUrlHandlers ] OneSignal.addListener(\'onClick\')', error))
        }
    }, [handleUrlRedirection, handleUrl])
}
